/**
 * <TutorMonthResetDialog> — « Nouveau mois, nouveau départ », à la première visite du mois
 * (template).
 *
 * L'ancienne `TutorRankingResetModal.js`, en sourdine : le rang et le score du mois passé, puis ce
 * que le Top 3 gagne — « tu gagnes 3 € de bonus par heure de live pendant les deux prochaines
 * semaines » quand le masteur y est, « vise le Top 3 » sinon. Ni champion, ni emoji, ni confetti
 * (livre V16 p. 35 : le tuteur reçoit l'arène en sourdine). Un seul geste, « C'est parti » : il
 * ferme et l'écran note la boîte comme vue (l'ancien `markResetModalShown`).
 *
 * L'ancienne boîte gardait un message à part pour novembre 2025 (pas de bonus ce mois-là) : il est
 * passé, il n'est pas repris.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"
import { View } from "react-native"

import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { type UITutorMonthReset } from "../../../ports/primary/ui/types/tutorCoquilleAccueil"
import { percentLabel } from "../../../ports/primary/ui/formatters/tutorScoreFormatter"

export type TutorMonthResetDialogProps = {
    /** Le rang du mois passé. `null` : la boîte est fermée. */
    reset: UITutorMonthReset | null
    onClose: () => void
    testID?: string
}

export function TutorMonthResetDialog(props: TutorMonthResetDialogProps) {
    const { reset, onClose, testID } = props

    return (
        <ConfirmDialog
            visible={reset != null}
            title="Nouveau mois, nouveau départ"
            message={
                reset?.topThree === true
                    ? "Tu fais partie du Top 3 : tu gagnes 3\u00A0€ de bonus par heure de live pendant les deux prochaines semaines. Continue comme ça."
                    : "Les masteurs du Top 3 gagnent 3\u00A0€ de bonus par heure de live pendant deux semaines. Améliore ton score ce mois-ci pour décrocher ce bonus."
            }
            confirmLabel="C'est parti"
            danger={false}
            hideCancel
            onConfirm={onClose}
            onCancel={onClose}
            testID={testID}
        >
            {reset != null && (
                <View style={styles.rank}>
                    <Stack gap="0" align="center">
                        <Text size="text-3xl" weight="bold" color="text-primary">
                            {reset.rank != null ? `#${reset.rank}` : "-"}
                        </Text>
                        <Text size="text-sm" color="text-muted">
                            Classement du mois dernier
                        </Text>
                        <Text size="text-md" weight="semibold" color="text-primary">
                            {`Score : ${percentLabel(reset.performance)}`}
                        </Text>
                    </Stack>
                </View>
            )}
        </ConfirmDialog>
    )
}

const styles = StyleSheet.create((theme) => ({
    rank: {
        padding: theme.spacing["4"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-subtle"],
    },
}))
